import React, { useState, useEffect } from "react";
import { ref, onValue } from "firebase/database";
import { database } from "../firebase/firebaseConfig";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import NightingaleChart from "./NightingaleChart";

const Dashboard = () => {
  const [stats, setStats] = useState({ bookings: 0, requests: 0, reviews: 0 }); // Include reviews in the stats state
  const [reviews, setReviews] = useState([]); // State to hold customer reviews
  const [userName, setUserName] = useState(""); // State to hold the logged-in user's name
  const [userIp, setUserIp] = useState(""); // State to hold the user's IP address
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth();

    // Check if user is authenticated
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserName(user.displayName || user.email || "User");
      } else {
        // If not authenticated, redirect to login
        navigate("/");
      }
    });

    // Fetch stats from Firebase
    const bookingsRef = ref(database, "bookings/");
    onValue(bookingsRef, (snapshot) => {
      const bookingsData = snapshot.val();
      const bookingsCount = bookingsData ? Object.keys(bookingsData).length : 0;

      const contactsRef = ref(database, "contacts/");
      onValue(contactsRef, (snapshot) => {
        const contactsData = snapshot.val();
        const contactsCount = contactsData ? Object.keys(contactsData).length : 0;

        setStats({
          bookings: bookingsCount,
          requests: contactsCount,
          reviews: reviews.length, // Add reviews length here
        });
      });
    });

    // Fetch customer reviews from Firebase
    const reviewsRef = ref(database, "testimonials/");
    onValue(reviewsRef, (snapshot) => {
      const reviewsData = snapshot.val();
      const reviewsList = reviewsData
        ? Object.keys(reviewsData).map((key) => reviewsData[key])
        : [];
      setReviews(reviewsList);
      // Update reviews count in stats
      setStats((prevStats) => ({
        ...prevStats,
        reviews: reviewsList.length,
      }));
    });

    // Fetch user's IP address
    const fetchIpAddress = async () => {
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        const data = await response.json();
        setUserIp(data.ip);
      } catch (error) {
        console.error("Error fetching IP address:", error);
      }
    };

    fetchIpAddress();

    // Auto logout logic
    const handleActivity = () => setLastActivityTime(Date.now());
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);

    const interval = setInterval(() => {
      const currentTime = Date.now();
      if (currentTime - lastActivityTime > 2 * 60 * 1000) {
        signOut(auth).then(() => navigate("/"));
      }
    }, 1000);

    return () => {
      unsubscribe(); // Clean up the auth state listener
      clearInterval(interval);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
    };
  }, [lastActivityTime, navigate, reviews.length]); // Added reviews.length to dependency array

  return (
    <div className="p-6 bg-gradient-to-r from-indigo-500 to-purple-600 min-h-screen flex flex-col relative">
      <button
        onClick={() => {
          const auth = getAuth();
          signOut(auth).then(() => navigate("/"));
        }}
        className="absolute top-4 right-4 bg-red-500 text-white py-2 px-4 rounded-lg shadow-md hover:bg-red-600 transition duration-200"
      >
        Logout
      </button>

      <h1 className="text-4xl font-extrabold text-center text-white mb-6">Dashboard</h1>
      <div className="text-xl font-medium text-center text-gray-200 mb-4">
        Welcome, <span className="text-yellow-300">{userName}</span>!
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div
          className="bg-white p-6 shadow-lg rounded-lg text-center transform hover:scale-105 transition duration-200 cursor-pointer"
          onClick={() => navigate("/bookings")}
        >
          <p className="text-lg font-semibold text-gray-700">Total Bookings</p>
          <p className="text-3xl font-bold text-indigo-500">{stats.bookings}</p>
        </div>
        <div
          className="bg-white p-6 shadow-lg rounded-lg text-center transform hover:scale-105 transition duration-200 cursor-pointer"
          onClick={() => navigate("/contacts")}
        >
          <p className="text-lg font-semibold text-gray-700">Contact Requests</p>
          <p className="text-3xl font-bold text-indigo-500">{stats.requests}</p>
        </div>
        <div
          className="bg-white p-6 shadow-lg rounded-lg text-center transform hover:scale-105 transition duration-200 cursor-pointer"
          onClick={() => navigate("/reviews")}
        >
          <p className="text-lg font-semibold text-gray-700">Customer Reviews</p>
          <p className="text-3xl font-bold text-indigo-500">{stats.reviews}</p>
        </div>
      </div>

      <div className="mt-10 bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-700 text-center mb-6">
          Visual Analytics
        </h2>
        <NightingaleChart stats={stats} />
      </div>

      <div className="mt-6 text-center text-sm text-gray-300">
        <p>IP Address: <span className="text-white">{userIp}</span></p>
      </div>
    </div>
  );
};

export default Dashboard;
